// src/pages/AdminDigital.jsx
// STATUS: active | UPDATED: 2026-08-29
// LOG:
//   2026-08-29  New. The first screen to show Jazz.
//
// Three dealerships he already knows, already scored, with what is wrong with
// each and what it is worth to fix.
//
// WHY THIS EXISTS AND WHY IT LOOKS LIKE THIS
//
// Jazz has not seen any of this and a tour of admin screens will not land. What
// lands is his own stores on a screen with numbers against them. This page asks
// nothing of him and explains itself, which is the opposite of a dashboard that
// asks him to imagine what it looks like once it is full.
//
// So there is no chrome, no filters, no date pickers and no configuration. It is
// a document about six things.
//
// THE PATTERN IS THE PRODUCT
//
// The single most valuable line on this page is that two of the three share a
// website vendor and therefore share the same three failures. That is what turns
// a consulting job into something repeatable, and it is computed rather than
// asserted: if a third Dealer Spike store lands tomorrow the count moves on its
// own.
//
// NO AVERAGING ACROSS LOCATIONS THAT DISAGREE. Rocky Ridge's six buildings all
// score 90 because they run one template. If they ever diverge, the spread is
// shown instead of a mean, because a mean would hide the one store that broke.

import { useCallback, useEffect, useState } from 'react'
import {
  Box, Flex, HStack, VStack, Text, Icon, Link, Spinner, Alert, AlertIcon, Divider,
} from '@chakra-ui/react'
import { TrendingUp, MapPin, ExternalLink, Layers } from 'lucide-react'
import { supabase } from '../lib/supabase'

const CATS = [
  ['found', 'Found'], ['fast', 'Fast'], ['trusted', 'Trusted'],
  ['shareable', 'Shareable'], ['converting', 'Converting'],
]

function Bar({ value, worst }) {
  return (
    <Box h="6px" bg="surface" borderRadius="1px" overflow="hidden" flex={1}>
      <Box h="100%" w={`${value ?? 0}%`} bg={worst ? 'accent.500' : 'ink'} borderRadius="1px" />
    </Box>
  )
}

function Business({ biz }) {
  const reports = biz.locations.map((l) => l.report).filter(Boolean)
  if (reports.length === 0) return null

  const scores = reports.map((r) => r.overall)
  const min = Math.min(...scores)
  const max = Math.max(...scores)
  const spread = min !== max

  // Categories are identical across a template built group, so the first report
  // represents them. When they diverge the per location list below shows it.
  const cat = reports[0].scores || {}
  const lowest = CATS.reduce((a, [k]) => (cat[k] < (cat[a] ?? 101) ? k : a), CATS[0][0])
  const high = (reports[0].findings || []).filter((f) => f.severity === 'high')
  const platform = reports[0].signals?.platform?.name || null

  return (
    <Box borderTop="1px solid" borderColor="line" pt={7} pb={2}>
      <Flex justify="space-between" align="flex-start" gap={5} wrap="wrap" mb={5}>
        <Box>
          <Text fontSize="display-sm" fontWeight={600} color="ink" letterSpacing="-0.02em" lineHeight={1.15}>
            {biz.name}
          </Text>
          <HStack spacing={2} mt={1.5} color="inkMuted">
            <Icon as={MapPin} boxSize={3.5} />
            <Text fontSize="body-sm">
              {biz.locations.length} {biz.locations.length === 1 ? 'location' : 'locations'}
              {platform ? ` · built on ${platform}` : ''}
            </Text>
          </HStack>
        </Box>
        <Flex align="baseline" gap={2}>
          <Text fontSize="48px" fontWeight={700} color="ink" lineHeight={0.9} fontVariantNumeric="tabular-nums">
            {spread ? `${min}–${max}` : min}
          </Text>
          <Text fontSize="lg" color="inkMuted">/100</Text>
        </Flex>
      </Flex>

      <VStack align="stretch" spacing={2} mb={5}>
        {CATS.map(([k, label]) => (
          <Flex key={k} align="center" gap={3}>
            <Text minW="86px" fontSize="12px" fontFamily="mono" letterSpacing="0.06em" textTransform="uppercase" color="inkMuted">
              {label}
            </Text>
            <Bar value={cat[k]} worst={k === lowest} />
            <Text minW="30px" textAlign="right" fontSize="body-sm" fontWeight={600} color="ink" fontVariantNumeric="tabular-nums">
              {cat[k]}
            </Text>
          </Flex>
        ))}
      </VStack>

      {high.length > 0 && (
        <Box bg="surface" borderRadius="card" p={5} mb={4}>
          <Text textStyle="eyebrow" color="accent.500" mb={3}>
            Costing them money now
          </Text>
          <VStack align="stretch" spacing={3}>
            {high.map((f) => (
              <Box key={f.title}>
                <Text fontSize="body-sm" fontWeight={600} color="ink" lineHeight={1.4}>
                  {f.title}
                </Text>
                <Text fontSize="12px" color="inkMuted" lineHeight={1.55} mt={0.5} maxW="measure">
                  {f.detail}
                </Text>
              </Box>
            ))}
          </VStack>
        </Box>
      )}

      <VStack align="stretch" spacing={1}>
        {biz.locations.map((l) => (
          <Flex key={l.id} align="center" gap={3} py={1.5}>
            <Text fontSize="body-sm" color="ink" minW="130px">
              {l.name}
              {l.state ? <Box as="span" color="inkMuted">, {l.state}</Box> : null}
            </Text>
            <Text fontSize="body-sm" fontWeight={600} color="ink" minW="30px" fontVariantNumeric="tabular-nums">
              {l.report ? l.report.overall : '--'}
            </Text>
            {l.report && (
              <Link
                href={`/report/${l.report.share_token}/`}
                isExternal
                fontSize="12px"
                color="accent.500"
                fontWeight={600}
                display="inline-flex"
                alignItems="center"
                gap={1}
              >
                The report Jazz sends <ExternalLink size={11} />
              </Link>
            )}
          </Flex>
        ))}
      </VStack>
    </Box>
  )
}

export default function AdminDigital() {
  const [state, setState] = useState({ loading: true, rows: [], error: null })

  const load = useCallback(async () => {
    const { data, error } = await supabase
      .from('dealerships')
      .select('id, name, slug, website, locations(id, name, city, state, is_primary, storefront_reports(id, overall, scores, findings, signals, share_token, created_at))')
      .order('name')

    if (error) {
      setState({ loading: false, rows: [], error: 'Could not load the dealerships.' })
      return
    }

    const rows = (data || [])
      .map((d) => ({
        ...d,
        locations: (d.locations || [])
          .map((l) => ({
            ...l,
            report: (l.storefront_reports || [])
              .slice()
              .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0] || null,
          }))
          .sort((a, b) => (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0) || a.name.localeCompare(b.name)),
      }))
      .filter((d) => d.locations.some((l) => l.report))

    setState({ loading: false, rows, error: null })
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const { rows } = state
  const allReports = rows.flatMap((d) => d.locations.map((l) => l.report).filter(Boolean))

  // Counted, not asserted. A third Dealer Spike store moves this on its own.
  const byPlatform = {}
  for (const d of rows) {
    const p = d.locations.find((l) => l.report)?.report?.signals?.platform?.name
    if (p) byPlatform[p] = (byPlatform[p] || 0) + 1
  }
  const shared = Object.entries(byPlatform).filter(([, n]) => n > 1)[0] || null

  return (
    <VStack align="stretch" spacing={0} maxW="sheet">
      <Box mb={7}>
        <HStack spacing={2} mb={2}>
          <Icon as={TrendingUp} boxSize={4} color="accent.500" />
          <Text textStyle="eyebrow" color="accent.500">
            Janda Digital
          </Text>
        </HStack>
        <Text textStyle="pageTitle">Dealerships we have looked at</Text>
        <Text fontSize="body" color="inkMuted" lineHeight={1.65} maxW="measure" mt={3}>
          Real audits of their live websites, run the same way every time. Every
          finding below is work Janda Digital can be paid to do, and every one of
          these dealerships can be sent their own report today.
        </Text>
      </Box>

      {state.loading && (
        <Flex justify="center" py={12}>
          <Spinner size="md" color="ink" thickness="3px" />
        </Flex>
      )}

      {state.error && (
        <Alert status="error" borderRadius="md" fontSize="sm">
          <AlertIcon />
          {state.error}
        </Alert>
      )}

      {!state.loading && !state.error && rows.length === 0 && (
        <Text fontSize="body-sm" color="inkMuted">
          Nothing audited yet. Add a dealership, give a location its page address and run a report.
        </Text>
      )}

      {!state.loading && allReports.length > 0 && (
        <>
          <HStack spacing={6} pb={6} wrap="wrap">
            <Box>
              <Text fontSize="display-sm" fontWeight={700} color="ink" lineHeight={1} fontVariantNumeric="tabular-nums">
                {rows.length}
              </Text>
              <Text fontSize="12px" color="inkMuted" mt={1}>
                businesses
              </Text>
            </Box>
            <Box>
              <Text fontSize="display-sm" fontWeight={700} color="ink" lineHeight={1} fontVariantNumeric="tabular-nums">
                {allReports.length}
              </Text>
              <Text fontSize="12px" color="inkMuted" mt={1}>
                locations audited
              </Text>
            </Box>
            <Box>
              <Text fontSize="display-sm" fontWeight={700} color="accent.500" lineHeight={1} fontVariantNumeric="tabular-nums">
                {allReports.reduce((a, r) => a + (r.findings || []).filter((f) => f.severity === 'high').length, 0)}
              </Text>
              <Text fontSize="12px" color="inkMuted" mt={1}>
                things costing them money
              </Text>
            </Box>
          </HStack>

          {shared && (
            <Box bg="ink" color="onInk" borderRadius="card" p={{ base: 5, md: 6 }} mb={2}>
              <HStack spacing={2} mb={2}>
                <Icon as={Layers} boxSize={4} color="onInkMuted" />
                <Text textStyle="eyebrow" color="onInkMuted">
                  The pattern
                </Text>
              </HStack>
              <Text fontSize="xl" fontWeight={600} lineHeight={1.3} mb={2.5} maxW="34ch">
                {shared[1]} of these {rows.length} run on {shared[0]}, and they fail in exactly the same places
              </Text>
              <Text fontSize="body-sm" color="onInkMuted" lineHeight={1.65} maxW="measure">
                Different states, different brands, different owners, and the same three
                gaps in the same order. That means nobody at those stores did anything
                wrong, the fix is already known before we walk in, and the second one
                costs almost nothing to deliver. It is the difference between a
                consulting job and something Janda can sell twice.
              </Text>
            </Box>
          )}

          <Box mt={4}>
            {rows.map((biz) => <Business key={biz.id} biz={biz} />)}
          </Box>

          <Divider borderColor="line" mt={8} mb={5} />
          <Text fontSize="12px" color="inkMuted" lineHeight={1.6} maxW="measure">
            Scores come from one page load, a DNS lookup and Google PageSpeed where it is
            available. Checks that could not run are left out of the calculation rather
            than counted against the dealership.
          </Text>
        </>
      )}
    </VStack>
  )
}
